import { FileInput } from '@mi-front/ui';
import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { ValidateAndUpload } from './ValidateAndUpload';

export function ValidateAndUploadView<T>({
  allowedExtensions,
  validateFn,
  importFn,
  renderPreview,
}: {
  allowedExtensions: string[];
  validateFn: (file: File) => Promise<T>;
  importFn: (file: File, validationResult: T) => Promise<unknown>;
  renderPreview: (validationResult: T) => React.ReactNode;
}) {
  const { $t } = useIntl();
  const [file, setFile] = useState<File | undefined>();
  const [validationState, setValidatingState] = useState<'idle' | 'loading' | 'done'>('idle');
  const [validationResult, setValidationResult] = useState<T | undefined>();

  const memoValidateFn = useCallback(() => {
    setValidatingState('loading');

    return validateFn(file!)
      .then((result) => {
        setValidationResult(result);

        return result;
      })
      .finally(() => setValidatingState('done'));
  }, [file]);

  const memoImportFn = useCallback(() => {
    return importFn(file!, validationResult!);
  }, [file, validationResult]);

  return (
    <div>
      {validationState !== 'done' && (
        <FileInput
          loading={validationState === 'loading'}
          name="files"
          className="mr-2"
          onChange={(files) => {
            files[0] && setFile(files[0]);
          }}
          label={$t(
            {
              id: 'common.validateFileLabel',
              defaultMessage: 'Validar archivo {extensions}',
            },
            { extensions: allowedExtensions.join('/') },
          )}
          accept={allowedExtensions.join(',')}
        />
      )}
      {file && (
        <ValidateAndUpload
          file={file}
          onReset={() => {
            setFile(undefined);
            setValidatingState('idle');
          }}
          validateFn={memoValidateFn}
          importFn={memoImportFn}
          renderPreview={renderPreview}
        />
      )}
    </div>
  );
}
